import React, { createContext, useContext, useState, useCallback } from "react";
import { api } from "@/lib/api";

const JourneyContext = createContext(null);
export const useJourney = () => useContext(JourneyContext);

export function JourneyProvider({ children }) {
  const [session, setSession] = useState(null);
  const [stage, setStage] = useState(0);
  const [busy, setBusy] = useState(false);
  const [clientType, setClientType] = useState(null); // first_home_buyer | home_buyer | investor

  const data = session?.data || {};

  const start = useCallback(async (consultant_name, client_name) => {
    const s = await api.createSession({ consultant_name, client_name, client_type: clientType || "home_buyer" });
    setSession(s);
    setStage(1);
    return s;
  }, [clientType]);

  const resume = useCallback(async (id) => {
    const s = await api.getSession(id);
    setSession(s);
    setClientType(s.client_type || "home_buyer");
    setStage(s.current_stage || 1);
    return s;
  }, []);

  const persistStage = useCallback(async (idx) => {
    if (!session) return;
    api.patchSession(session.id, { current_stage: idx }).catch(() => {});
  }, [session]);

  const goto = useCallback((idx) => {
    setStage(idx);
    persistStage(idx);
  }, [persistStage]);

  const next = useCallback(() => goto(stage + 1), [stage, goto]);
  const back = useCallback(() => goto(Math.max(1, stage - 1)), [stage, goto]);

  const mergeData = useCallback((patch) => {
    setSession((prev) => (prev ? { ...prev, data: { ...(prev.data || {}), ...patch } } : prev));
  }, []);

  const refresh = useCallback(async () => {
    if (!session) return;
    const s = await api.getSession(session.id);
    setSession(s);
    return s;
  }, [session]);

  const addReward = useCallback((reward) => {
    setSession((prev) => {
      if (!prev) return prev;
      const existing = prev.data?.rewards || [];
      if (existing.some((r) => r.name === reward.name)) return prev;
      const rewards = [...existing, reward];
      api.patchSession(prev.id, { data: { rewards } }).catch(() => {});
      return { ...prev, data: { ...(prev.data || {}), rewards } };
    });
  }, []);

  const value = {
    session, setSession, stage, setStage, data, busy, setBusy,
    clientType, setClientType,
    start, resume, goto, next, back, mergeData, refresh, addReward,
  };
  return <JourneyContext.Provider value={value}>{children}</JourneyContext.Provider>;
}
